import { useFormik } from 'formik'
import { FC, useState } from 'react'
import * as Yup from 'yup'
import { ButtonType, OptionsHelper, showAlert } from '../../../../../../_metronic/helpers'
import {
  Button,
  FormDateInput,
  FormInput,
  FormSelect,
  FormToggle,
  IOption,
  InfoAlert,
  LoadingIndicator,
  useModal,
} from '../../../../../components'
import { ErrosVinculos } from '../../components/ErrosVinculos'
import { FeriadoView, TipoFeriado } from '../../core/_models'
import { createFeriadoEmpresa, updateFeriado } from '../../core/_requests'
import {
  ErrosValidacao,
  TIPO_FERIADO_LABELS,
  extrairErrosValidacao,
} from '../../core/escalaHelpers'

type Props = {
  /** Quando presente, o modal edita (`PUT /feriados/{id}`) em vez de criar. */
  feriado?: FeriadoView
  onSuccess: () => void
}

type ValoresFormulario = {
  nome: string
  tipo: IOption | null
  empresa: IOption | null
  data: string
  intervalo: string
  anual: boolean
}

const TIPO_OPTIONS: IOption[] = Object.entries(TIPO_FERIADO_LABELS).map(([value, label]) => ({
  value,
  label,
}))

export const FeriadoFormModal: FC<Props> = ({ feriado, onSuccess }) => {
  const { closeModal } = useModal()
  const [erros, setErros] = useState<ErrosValidacao | null>(null)

  const edicao = Boolean(feriado)

  const formik = useFormik<ValoresFormulario>({
    initialValues: {
      nome: feriado?.nome ?? '',
      tipo: TIPO_OPTIONS.find((opcao) => opcao.value === feriado?.tipo) ?? null,
      empresa: feriado?.empresa
        ? {
            value: feriado.empresa.id,
            label: feriado.empresa.nomeFantasia ?? feriado.empresa.nome ?? '',
          }
        : null,
      data: feriado?.data ?? '',
      intervalo: '',
      anual: feriado?.anual ?? false,
    },
    validationSchema: Yup.object({
      nome: Yup.string().max(100, 'Máximo de 100 caracteres').required('Informe o nome'),
      tipo: Yup.object().nullable().required('Informe o tipo do feriado'),
      empresa: edicao
        ? Yup.object().nullable()
        : Yup.object().nullable().required('Informe a instituição'),
      data: Yup.string().required('Informe a data'),
      intervalo: Yup.string().test(
        'intervalo-depois-da-data',
        'O fim do período não pode ser anterior à data',
        function (valor) {
          return !valor || !this.parent.data || valor >= this.parent.data
        }
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setErros(null)
      setSubmitting(true)

      try {
        if (feriado) {
          await updateFeriado(feriado.id, {
            tipo: values.tipo!.value as TipoFeriado,
            nome: values.nome,
            data: values.data,
            anual: values.anual,
          })
        } else {
          await createFeriadoEmpresa(Number(values.empresa!.value), {
            tipo: values.tipo!.value as TipoFeriado,
            nome: values.nome,
            data: values.data,
            intervalo: values.intervalo || null,
            anual: values.anual,
          })
        }

        showAlert({
          title: 'Pronto!',
          text: edicao ? 'Feriado atualizado.' : 'Feriado cadastrado.',
          icon: 'success',
        })
        onSuccess()
        closeModal()
      } catch (error) {
        setErros(extrairErrosValidacao(error))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const mensagensDeErro = erros
    ? erros.todas.length
      ? erros.todas
      : erros.mensagem
      ? [erros.mensagem]
      : []
    : []

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <ErrosVinculos titulo='Não foi possível salvar:' itens={mensagensDeErro} />

      <div className='row'>
        <FormInput
          formik={formik}
          name='nome'
          label='Nome do feriado'
          placeholder='Ex.: Carnaval'
          className='col-md-6 mb-5'
          isRequired
        />
        <FormSelect
          formik={formik}
          name='tipo'
          label='Tipo'
          options={TIPO_OPTIONS}
          className='col-md-6 mb-5'
          isRequired
        />
      </div>

      {!edicao && (
        <FormSelect
          formik={formik}
          name='empresa'
          label='Instituição'
          placeholder='Selecione a instituição'
          fetchOptions={OptionsHelper.getOptionsEmpresasAdmin}
          className='mb-5'
          isRequired
          isClearable
        />
      )}

      <div className='row'>
        <FormDateInput
          formik={formik}
          name='data'
          label='Data'
          className='col-md-6 mb-5'
          isRequired
        />
        {!edicao && (
          <FormDateInput
            formik={formik}
            name='intervalo'
            label='Fim do período'
            className='col-md-6 mb-5'
            minDate={formik.values.data || undefined}
            infoText='Preencha para cadastrar um período inteiro (ex.: carnaval), um feriado por dia.'
          />
        )}
      </div>

      <FormToggle
        formik={formik}
        name='anual'
        label='Repete todo ano na mesma data'
        className='mb-5'
      />

      {edicao && (
        <InfoAlert variant='info' title='Edição de um único feriado'>
          A alteração vale só para este registro. Feriados cadastrados como período viraram um
          registro por dia — ajuste cada um, ou exclua e cadastre o período de novo.
        </InfoAlert>
      )}

      <div className='d-flex justify-content-end pt-3'>
        <Button
          className='btn-secondary me-2'
          type={ButtonType.Reset}
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          Cancelar
        </Button>
        <Button className='btn-primary' type={ButtonType.Submit} disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : 'Salvar feriado'}
        </Button>
      </div>
    </form>
  )
}
